import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthUser } from "@/types/auth";

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;

  login: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (hydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      hasHydrated: false,

      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setUser: (user) => set({ user }),

      setLoading: (isLoading) => set({ isLoading }),

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: "yogo-auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

// Reactive hook — components should use this instead of a non-reactive
// persist.hasHydrated() call, same pattern as apps/driver/src/store/authStore.ts
export function useHasHydrated() {
  return useAuthStore((s) => s.hasHydrated);
}

// ── Cross-tab sync ────────────────────────────────────────────────────────
// Zustand's persist middleware writes to localStorage but does NOT push
// live updates to other open tabs. Without this, a background token
// refresh in one tab silently invalidates every other tab's in-memory
// copy of the refresh token (since the server only keeps one valid
// refresh token per user by design) — the other tab then fails its next
// refresh attempt, force-logs-out locally without telling the server,
// and the user gets blocked on re-login by a session that's still alive
// in a tab they didn't even know was open.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "yogo-auth-storage" || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue);
      const state = parsed?.state;
      if (state?.accessToken && state?.refreshToken) {
        useAuthStore.setState({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          user: state.user ?? useAuthStore.getState().user,
          isAuthenticated: state.isAuthenticated ?? true,
        });
      } else if (state && !state.isAuthenticated) {
        // Another tab logged out — mirror that here too.
        useAuthStore.getState().logout();
      }
    } catch {
      // Malformed storage event — ignore, don't crash the tab over it.
    }
  });
}

// Day 7 complete
// Day 83 fix — hasHydrated tracking added to fix refresh false-logout
